import * as R from "ramda";
import { isAdded, isInLupa, isRemoved } from "@/css/label";
import {
  getOppisopimusPerusteluLomake,
  getVaativaErityinenTukilomake,
  getVankilaopetusPerustelulomake
} from "../muut";
import { sortArticlesByKoodiarvo } from "../../utils";
import { PaikallisenTietovarastonAvain } from "@/enums";
import localForage from "localforage";

function getStructureByKoodiarvo(
  perusteltavaTeksti,
  isAddition,
  isReadOnly,
  lomakkeet
) {
  return perusteltavaTeksti !== ""
    ? [
        {
          anchor: "A",
          components: [
            {
              name: "StatusTextRow",
              properties: {
                isReadOnly,
                title: perusteltavaTeksti
              }
            }
          ],
          categories: isAddition ? lomakkeet.lisays : lomakkeet.poisto
        }
      ]
    : isAddition
      ? lomakkeet.lisays
      : lomakkeet.poisto;
}

export function getDefaultAdditionForm(isReadOnly) {
  return [
    {
      anchor: "perustelut",
      layout: {
        margins: { top: "small" },
        strategy: {
          key: "groups"
        }
      },
      components: [
        {
          anchor: "A",
          name: "TextBox",
          properties: {
            isReadOnly,
            title: "Perustele",
            value: ""
          }
        }
      ]
    }
  ];
}

export function getDefaultRemovalForm(isReadOnly) {
  return getDefaultAdditionForm(isReadOnly);
}

function getMapping(vankilat, isReadOnly, locale, defaultRemovalForm) {
  return [
    {
      koodiarvot: [5],
      lomakkeet: {
        lisays: getVankilaopetusPerustelulomake(vankilat, isReadOnly, locale),
        poisto: defaultRemovalForm
      }
    },
    {
      koodiarvot: [2, 16, 17, 18, 19, 20, 21],
      lomakkeet: {
        lisays: getVaativaErityinenTukilomake(isReadOnly),
        poisto: defaultRemovalForm
      }
    },
    {
      koodiarvot: [1],
      lomakkeet: {
        lisays: getOppisopimusPerusteluLomake(isReadOnly),
        poisto: defaultRemovalForm
      }
    }
  ];
}

function getCategoriesByKoodiarvo(
  koodiarvo,
  mapping,
  defaultAdditionForm,
  defaultRemovalForm
) {
  const koodiArvoInteger = parseInt(koodiarvo, 10);

  const obj = R.find(
    R.compose(R.includes(koodiArvoInteger), R.prop("koodiarvot")),
    mapping
  );
  return obj
    ? obj.lomakkeet
    : {
        lisays: defaultAdditionForm,
        poisto: defaultRemovalForm
      };
}

function divideArticles(
  articles,
  relevantCodes,
  relevantMaaraykset,
  mapping,
  defaultAdditionForm,
  defaultRemovalForm,
  locale
) {
  const localeUpper = R.toUpper(locale);
  const dividedArticles = {};
  const sortedArticles = sortArticlesByKoodiarvo(articles);
  R.forEach(article => {
    const { metadata } = article;
    const kasite = metadata[localeUpper].kasite;
    const kuvaus = metadata[localeUpper].kuvaus;
    const isInLupa = !!R.find(R.propEq("koodiarvo", article.koodiarvo))(
      relevantMaaraykset
    );
    if (
      // Relevant codes includes only the koodiarvot that are selected on page 1
      R.includes(article.koodiarvo, relevantCodes) &&
      (kuvaus || article.koodiarvo === "22") &&
      kasite &&
      (isInLupa || article.koodiarvo !== "15")
    ) {
      dividedArticles[kasite] = dividedArticles[kasite] || [];
      dividedArticles[kasite].push({
        article,
        lomakkeet: getCategoriesByKoodiarvo(
          article.koodiarvo,
          mapping,
          defaultAdditionForm,
          defaultRemovalForm
        )
      });
    }
  }, sortedArticles);
  return dividedArticles;
}

function getCategoryData(dividedArticles, areaCode) {
  switch (areaCode) {
    case "01":
      return {
        key: "laajennettuOppisopimuskoulutus",
        articles: dividedArticles.laajennettu || [],
        componentName: "StatusTextRow"
      };
    case "02":
      return {
        key: "vaativatuki",
        articles: R.concat(
          dividedArticles.vaativa_1 || [],
          dividedArticles.vaativa_2 || []
        ),
        componentName: "StatusTextRow"
      };
    case "03":
      return {
        key: "sisaoppilaitos",
        articles: dividedArticles.sisaoppilaitos || [],
        componentName: "StatusTextRow"
      };
    case "04":
      return {
        key: "vankila",
        articles: dividedArticles.vankila || [],
        componentName: "StatusTextRow"
      };
    case "05":
      return {
        key: "urheilu",
        articles: dividedArticles.urheilu || [],
        componentName: "StatusTextRow"
      };
    case "06":
      return {
        key: "yhteistyo",
        articles: dividedArticles.yhteistyo || [],
        componentName: "StatusTextRow"
      };
    case "07":
      return {
        key: "muumaarays",
        articles: dividedArticles.muumaarays || [],
        componentName: "StatusTextRow"
      };
    default:
      return null;
  }
}

function getReasoningForm(
  areaCode,
  defaultAdditionForm,
  defaultRemovalForm,
  changeObjects,
  maaraykset,
  mapping,
  muut,
  isReadOnly,
  locale
) {
  const relevantCodes = R.map(R.compose(R.view(R.lensIndex(2)), R.split(".")))(
    R.map(R.prop("anchor"), R.compose(R.flatten, R.values)(changeObjects))
  );
  const relevantMaaraykset = R.filter(
    R.propEq("koodisto", "oivamuutoikeudetvelvollisuudetehdotjatehtavat")
  )(maaraykset);
  const dividedArticles = divideArticles(
    muut,
    relevantCodes,
    relevantMaaraykset,
    mapping,
    defaultAdditionForm,
    defaultRemovalForm,
    locale
  );
  const item = getCategoryData(dividedArticles, areaCode);
  const localeUpper = R.toUpper(locale);

  return item
    ? [
        {
          anchor: item.key,
          categories: R.map(({ article, lomakkeet }) => {
            const title =
              article.metadata[localeUpper].kuvaus ||
              article.metadata[localeUpper].nimi;
            const isInLupaBool = article
              ? !!R.find(koulutusala => {
                  return article.koodiarvo === koulutusala;
                }, article.voimassaAlkuPvm)
              : false;
            const anchorInit = `muut_${areaCode}.${item.key}.${article.koodiarvo}`;
            const changeObj = R.find(
              R.compose(R.startsWith(anchorInit), R.prop("anchor")),
              changeObjects
            );
            const isAddition = changeObj && changeObj.properties.isChecked;
            const perusteltavaTeksti =
              article.koodiarvo === "22" &&
              R.find(
                R.propEq("anchor", "muut_07.muumaarays.22.other.A"),
                changeObjects
              )
                ? R.find(
                    R.propEq("anchor", "muut_07.muumaarays.22.other.A"),
                    changeObjects
                  ).properties.value
                : "";
            const isReasoningRequired =
              R.path(
                ["properties", "metadata", "isReasoningRequired"],
                changeObj
              ) !== false;
            const labelClasses = {
              isInLupa: isInLupaBool
            };
            let structure = null;
            if (isReasoningRequired) {
              structure = {
                layout: {
                  indentation: "none",
                  margins: { top: "large" }
                },
                anchor: article.koodiarvo,
                meta: {
                  isInLupa: isInLupaBool,
                  koodiarvo: article.koodiarvo,
                  koodisto: article.koodisto
                },
                components: [
                  {
                    anchor: "A",
                    name: item.componentName,
                    properties: {
                      isReadOnly,
                      name: item.componentName,
                      title: title,
                      labelStyles: {
                        addition: isAdded,
                        removal: isRemoved,
                        custom: Object.assign(
                          {},
                          labelClasses.isInLupa ? isInLupa : {}
                        )
                      },
                      styleClasses: ["flex"],
                      statusTextStyleClasses: isAddition
                        ? ["text-green-600 pr-4 w-20 font-bold"]
                        : ["text-red-500 pr-4 w-20 font-bold"],
                      statusText: isAddition ? " LISÄYS:" : " POISTO:"
                    }
                  }
                ],
                categories: getStructureByKoodiarvo(
                  perusteltavaTeksti,
                  isAddition,
                  isReadOnly,
                  lomakkeet
                )
              };
            }
            return structure;
          }, item.articles).filter(Boolean)
        }
      ]
    : [];
}

export default async function getMuutPerustelulomake(
  data,
  { isReadOnly },
  locale,
  changeObjects = []
) {
  const muut = await localForage.getItem(PaikallisenTietovarastonAvain.MUUT);
  const vankilat = await localForage.getItem(
    PaikallisenTietovarastonAvain.VANKILAT
  );

  const defaultAdditionForm = getDefaultAdditionForm(isReadOnly);
  const defaultRemovalForm = getDefaultRemovalForm(isReadOnly);
  const mapping = getMapping(vankilat, isReadOnly, locale, defaultRemovalForm);

  return getReasoningForm(
    data.areaCode,
    defaultAdditionForm,
    defaultRemovalForm,
    changeObjects,
    data.maarayksetByKoodiarvo,
    mapping,
    muut,
    isReadOnly,
    R.toUpper(locale)
  );
}
